import type { RecipeId, StyleId, TextTransform } from '@/types';

/**
 * Style presets.
 *
 * Note what is NOT in this interface: a font family. Every style renders in
 * Helvetica Neue. Styles differ by weight, scale, tracking, case, overlay
 * treatment, and how much negative space they insist on — which is where the
 * visual difference between "Luxury" and "Bold" actually lives.
 */
export interface StylePreset {
  id: StyleId;
  label: string;
  blurb: string;

  headlineWeight: number;
  subtitleWeight: number;
  kickerWeight: number;

  /** Headline size as a fraction of canvas width, before fitting. */
  headlineScale: number;
  subtitleScale: number;
  kickerScale: number;

  headlineTracking: number;
  subtitleTracking: number;
  kickerTracking: number;

  headlineCase: TextTransform;
  kickerCase: TextTransform;

  headlineLeading: number;
  subtitleLeading: number;

  /** 0 = tight and dense, 1 = generous negative space. */
  airiness: number;
  /** Preferred scrim strength before contrast correction. */
  overlayStrength: number;
  /** Whether the style likes an accent-colored rule or bar. */
  useRule: boolean;
  /** Whether the style highlights key words in the accent color. */
  useEmphasis: boolean;
  /** Photo treatment nudges. */
  imageBrightness: number;
  imageContrast: number;
  imageSaturation: number;

  /** Recipes that suit this style, best first. */
  recipes: RecipeId[];
}

export const STYLE_PRESETS: Record<StyleId, StylePreset> = {
  professional: {
    id: 'professional',
    label: 'Professional',
    blurb: 'Confident, legible, quietly corporate.',
    headlineWeight: 700,
    subtitleWeight: 400,
    kickerWeight: 500,
    headlineScale: 0.082,
    subtitleScale: 0.032,
    kickerScale: 0.02,
    headlineTracking: -0.015,
    subtitleTracking: 0,
    kickerTracking: 0.18,
    headlineCase: 'none',
    kickerCase: 'upper',
    headlineLeading: 1.08,
    subtitleLeading: 1.42,
    airiness: 0.5,
    overlayStrength: 0.58,
    useRule: true,
    useEmphasis: false,
    imageBrightness: 0.98,
    imageContrast: 1.04,
    imageSaturation: 0.98,
    recipes: ['lower-third', 'side-panel', 'top-kicker', 'editorial-stack'],
  },

  minimal: {
    id: 'minimal',
    label: 'Minimal',
    blurb: 'Light weights, wide tracking, a lot of air.',
    headlineWeight: 300,
    subtitleWeight: 300,
    kickerWeight: 400,
    headlineScale: 0.062,
    subtitleScale: 0.026,
    kickerScale: 0.017,
    headlineTracking: 0.02,
    subtitleTracking: 0.03,
    kickerTracking: 0.34,
    headlineCase: 'none',
    kickerCase: 'upper',
    headlineLeading: 1.3,
    subtitleLeading: 1.6,
    airiness: 0.92,
    overlayStrength: 0.32,
    useRule: false,
    useEmphasis: false,
    imageBrightness: 1.02,
    imageContrast: 0.98,
    imageSaturation: 0.9,
    recipes: ['framed-minimal', 'corner-anchor', 'centered-hero', 'top-kicker'],
  },

  luxury: {
    id: 'luxury',
    label: 'Luxury',
    blurb: 'Light caps, very wide tracking, deep negative space.',
    headlineWeight: 300,
    subtitleWeight: 300,
    kickerWeight: 400,
    headlineScale: 0.058,
    subtitleScale: 0.024,
    kickerScale: 0.016,
    headlineTracking: 0.16,
    subtitleTracking: 0.08,
    kickerTracking: 0.46,
    headlineCase: 'upper',
    kickerCase: 'upper',
    headlineLeading: 1.42,
    subtitleLeading: 1.7,
    airiness: 1,
    overlayStrength: 0.46,
    useRule: true,
    useEmphasis: false,
    imageBrightness: 0.94,
    imageContrast: 1.06,
    imageSaturation: 0.82,
    recipes: ['centered-hero', 'framed-minimal', 'corner-anchor', 'lower-third'],
  },

  medical: {
    id: 'medical',
    label: 'Medical / Clinic',
    blurb: 'Clean, trustworthy, no icons or clinical clichés.',
    headlineWeight: 500,
    subtitleWeight: 300,
    kickerWeight: 500,
    headlineScale: 0.07,
    subtitleScale: 0.028,
    kickerScale: 0.018,
    headlineTracking: -0.005,
    subtitleTracking: 0.01,
    kickerTracking: 0.3,
    headlineCase: 'none',
    kickerCase: 'upper',
    headlineLeading: 1.16,
    subtitleLeading: 1.55,
    airiness: 0.86,
    overlayStrength: 0.5,
    useRule: true,
    useEmphasis: false,
    imageBrightness: 1.01,
    imageContrast: 1.02,
    imageSaturation: 0.86,
    recipes: ['side-panel', 'lower-third', 'framed-minimal', 'corner-anchor'],
  },

  editorial: {
    id: 'editorial',
    label: 'Editorial',
    blurb: 'Magazine stack — big head, tight leading, thin rule.',
    headlineWeight: 700,
    subtitleWeight: 400,
    kickerWeight: 500,
    headlineScale: 0.098,
    subtitleScale: 0.03,
    kickerScale: 0.019,
    headlineTracking: -0.028,
    subtitleTracking: 0.005,
    kickerTracking: 0.28,
    headlineCase: 'none',
    kickerCase: 'upper',
    headlineLeading: 0.98,
    subtitleLeading: 1.45,
    airiness: 0.36,
    overlayStrength: 0.62,
    useRule: true,
    useEmphasis: true,
    imageBrightness: 0.95,
    imageContrast: 1.1,
    imageSaturation: 0.92,
    recipes: ['editorial-stack', 'lower-third', 'split-band', 'top-kicker'],
  },

  modern: {
    id: 'modern',
    label: 'Modern',
    blurb: 'Medium weights, open leading, asymmetric placement.',
    headlineWeight: 500,
    subtitleWeight: 400,
    kickerWeight: 500,
    headlineScale: 0.078,
    subtitleScale: 0.03,
    kickerScale: 0.019,
    headlineTracking: -0.01,
    subtitleTracking: 0.01,
    kickerTracking: 0.26,
    headlineCase: 'none',
    kickerCase: 'upper',
    headlineLeading: 1.14,
    subtitleLeading: 1.5,
    airiness: 0.66,
    overlayStrength: 0.48,
    useRule: false,
    useEmphasis: true,
    imageBrightness: 1,
    imageContrast: 1.05,
    imageSaturation: 1.04,
    recipes: ['corner-anchor', 'side-panel', 'lower-third', 'editorial-stack'],
  },

  bold: {
    id: 'bold',
    label: 'Bold',
    blurb: 'Heavy caps, tight leading, maximum presence.',
    headlineWeight: 800,
    subtitleWeight: 500,
    kickerWeight: 700,
    headlineScale: 0.108,
    subtitleScale: 0.03,
    kickerScale: 0.02,
    headlineTracking: -0.035,
    subtitleTracking: 0.01,
    kickerTracking: 0.2,
    headlineCase: 'upper',
    kickerCase: 'upper',
    headlineLeading: 0.92,
    subtitleLeading: 1.4,
    airiness: 0.22,
    overlayStrength: 0.66,
    useRule: false,
    useEmphasis: true,
    imageBrightness: 0.9,
    imageContrast: 1.16,
    imageSaturation: 1.02,
    recipes: ['split-band', 'editorial-stack', 'lower-third', 'centered-hero'],
  },

  instagram: {
    id: 'instagram',
    label: 'Instagram',
    blurb: 'Punchy centered hero, built for the feed.',
    headlineWeight: 700,
    subtitleWeight: 400,
    kickerWeight: 500,
    headlineScale: 0.092,
    subtitleScale: 0.031,
    kickerScale: 0.019,
    headlineTracking: -0.022,
    subtitleTracking: 0.015,
    kickerTracking: 0.24,
    headlineCase: 'none',
    kickerCase: 'upper',
    headlineLeading: 1.04,
    subtitleLeading: 1.44,
    airiness: 0.44,
    overlayStrength: 0.56,
    useRule: false,
    useEmphasis: true,
    imageBrightness: 1.02,
    imageContrast: 1.08,
    imageSaturation: 1.1,
    recipes: ['centered-hero', 'lower-third', 'split-band', 'corner-anchor'],
  },

  corporate: {
    id: 'corporate',
    label: 'Corporate',
    blurb: 'Structured panel, disciplined alignment.',
    headlineWeight: 500,
    subtitleWeight: 400,
    kickerWeight: 500,
    headlineScale: 0.068,
    subtitleScale: 0.028,
    kickerScale: 0.018,
    headlineTracking: -0.008,
    subtitleTracking: 0.005,
    kickerTracking: 0.32,
    headlineCase: 'none',
    kickerCase: 'upper',
    headlineLeading: 1.18,
    subtitleLeading: 1.5,
    airiness: 0.62,
    overlayStrength: 0.54,
    useRule: true,
    useEmphasis: false,
    imageBrightness: 0.99,
    imageContrast: 1.03,
    imageSaturation: 0.92,
    recipes: ['side-panel', 'top-kicker', 'lower-third', 'framed-minimal'],
  },
};

export const STYLE_LIST = Object.values(STYLE_PRESETS);

export function getStyle(id: StyleId): StylePreset {
  return STYLE_PRESETS[id] ?? STYLE_PRESETS.professional;
}

export const RECIPE_LABELS: Record<RecipeId, string> = {
  'lower-third': 'Lower third',
  'editorial-stack': 'Editorial stack',
  'centered-hero': 'Centered hero',
  'side-panel': 'Side panel',
  'top-kicker': 'Top kicker',
  'framed-minimal': 'Framed minimal',
  'split-band': 'Split band',
  'corner-anchor': 'Corner anchor',
};
